/**
 * ElevenLabs Voice Library
 *
 * Fetches available voices from ElevenLabs API for professional phone greetings.
 * Uses real voice metadata from ElevenLabs instead of hardcoded data.
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';

export interface PresetVoice {
  id: string;
  elevenLabsId: string;
  name: string;
  description: string;
  category: string;
  labels: string[];
  previewUrl: string | null;
  gender?: string;
  accent?: string;
  age?: string;
  useCase?: string;
  featured?: boolean;
  source?: 'user' | 'library'; // Track voice source
}

const log = createComponentLogger('useElevenLabsVoices', 'voice-library');

/**
 * React hook to fetch available voices from ElevenLabs API
 * Combines user voices + library voices for maximum selection
 */
export function useElevenLabsVoices() {
  const [voices, setVoices] = useState<PresetVoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState({
    userVoices: 0,
    libraryVoices: 0,
    total: 0
  });

  useEffect(() => {
    async function fetchVoices() {
      try {
        setLoading(true);
        setError(null);

        log.info('Fetching voices from ElevenLabs API', { sources: ['user', 'library'] });

        // Fetch from both endpoints in parallel
        const [userResult, libraryResult] = await Promise.allSettled([
          supabase.functions.invoke('elevenlabs-list-voices', { method: 'GET' }),
          supabase.functions.invoke('elevenlabs-list-library-voices', { method: 'GET' })
        ]);

        const allVoices: PresetVoice[] = [];
        let userCount = 0;
        let libraryCount = 0;

        // Process user voices
        if (userResult.status === 'fulfilled') {
          const { data, error: userError } = userResult.value;
          if (data?.success && data.voices) {
            const userVoices = data.voices.map((v: PresetVoice) => ({ ...v, source: 'user' as const }));
            allVoices.push(...userVoices);
            userCount = userVoices.length;
            log.info('User voices loaded', { count: userCount });
          } else if (userError) {
            log.warn('Failed to load user voices', {}, userError);
          }
        } else {
          log.warn('User voices request failed', { reason: userResult.reason });
        }

        // Process library voices
        if (libraryResult.status === 'fulfilled') {
          const { data, error: libraryError } = libraryResult.value;
          if (data?.success && data.voices) {
            const libraryVoices = data.voices.map((v: PresetVoice) => ({ ...v, source: 'library' as const }));
            allVoices.push(...libraryVoices);
            libraryCount = libraryVoices.length;
            log.info('Library voices loaded', { count: libraryCount });
          } else if (libraryError) {
            log.warn('Failed to load library voices', {}, libraryError);
          }
        } else {
          log.warn('Library voices request failed', { reason: libraryResult.reason });
        }

        // Deduplicate by elevenLabsId (user voices take precedence)
        const uniqueVoices = Array.from(
          new Map(allVoices.map(v => [v.elevenLabsId, v])).values()
        );

        // Sort: User voices first, then library voices, then by featured status, then by name
        uniqueVoices.sort((a, b) => {
          if (a.source !== b.source) {
            return a.source === 'user' ? -1 : 1;
          }
          if (a.featured !== b.featured) {
            return a.featured ? -1 : 1;
          }
          return a.name.localeCompare(b.name);
        });

        setVoices(uniqueVoices);
        setStats({
          userVoices: userCount,
          libraryVoices: libraryCount,
          total: uniqueVoices.length
        });

        log.info('Voice library fetched successfully', {
          total: uniqueVoices.length,
          userVoices: userCount,
          libraryVoices: libraryCount
        });

        // Show error only if BOTH endpoints failed
        if (uniqueVoices.length === 0) {
          throw new Error('Failed to load voices from both sources');
        }

      } catch (err: any) {
        console.error('Error fetching ElevenLabs voices:', err);
        setError(err.message || 'Failed to load voices');
        setVoices([]);
      } finally {
        setLoading(false);
      }
    }

    fetchVoices();
  }, []);

  return { voices, loading, error, stats };
}

/**
 * Get voice by ElevenLabs ID from a list of voices
 */
export function getVoiceById(voices: PresetVoice[], elevenLabsId: string): PresetVoice | undefined {
  return voices.find((v) => v.elevenLabsId === elevenLabsId);
}

/**
 * Get voice display name
 */
export function getVoiceDisplayName(voice: PresetVoice): string {
  return voice.name;
}

/**
 * Get category badge color
 */
export function getCategoryColor(category: string): string {
  const colors: Record<string, string> = {
    professional: 'bg-blue-100 text-blue-700 border-blue-200',
    premade: 'bg-purple-100 text-purple-700 border-purple-200',
    high_quality: 'bg-green-100 text-green-700 border-green-200'
  };
  return colors[category.toLowerCase()] || 'bg-gray-100 text-gray-700 border-gray-200';
}
